import { RecurrenceDay } from "../../deals/api/useDeals";

export const ALL_DAYS: { value: RecurrenceDay; short: string }[] = [
  { value: "mon", short: "Mon" },
  { value: "tue", short: "Tue" },
  { value: "wed", short: "Wed" },
  { value: "thu", short: "Thu" },
  { value: "fri", short: "Fri" },
  { value: "sat", short: "Sat" },
  { value: "sun", short: "Sun" },
];

// Treats the value as local wall-clock — only used to auto-check the matching
// day pill; the small TZ inaccuracy is acceptable and the admin can adjust.
export function weekdayOf(datetimeLocal: string): RecurrenceDay | null {
  if (!datetimeLocal) return null;
  const d = new Date(datetimeLocal);
  if (Number.isNaN(d.getTime())) return null;
  return ALL_DAYS[(d.getDay() + 6) % 7].value;
}

export function summarizeRecurrence(
  days: RecurrenceDay[],
  endDate: string | null,
  startTime: string,
): string {
  if (days.length === 0) return "";
  const orderedShort = ALL_DAYS.filter(d => days.includes(d.value)).map(d => d.short).join(", ");
  const t = startTime ? new Date(startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
  const until = endDate ? `until ${endDate}` : "indefinitely";
  return `Repeats every ${orderedShort}${t ? ` at ${t}` : ""} ${until}.`;
}

/**
 * The end date a recurring deal gets when the admin doesn't pick one:
 * 31 December of the year the deal STARTS.
 *
 * Derived from the start date rather than today so a series created in late
 * December for a January start runs through that following year instead of
 * expiring days after it begins.
 *
 * `startTime` is a `datetime-local` already expressed in the venue's own
 * wall-clock, so its leading year is the venue's year — no timezone conversion
 * is needed. The timezone is only consulted for the fallback, where there is no
 * start date to read and "which year is it" genuinely depends on where you are.
 */
export function defaultRecurrenceEndDate(startTime: string, timezone: string): string {
  const fromStart = /^(\d{4})/.exec(startTime)?.[1];
  const year =
    fromStart ??
    new Intl.DateTimeFormat("en-CA", { timeZone: timezone || "UTC", year: "numeric" }).format(
      new Date(),
    );
  return `${year}-12-31`;
}
